import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import AppDownloadBanner from "components/marketing/AppDownloadBanner";
import { useAuth } from "./contexts/AuthContext";
import NotFound from "pages/NotFound";
import Landing from './pages/landing';
import PhotographerProfile from './pages/photographer-profile';
import BookingConfirmation from './pages/booking-confirmation';
import BookingWizard from './pages/booking-wizard';
import BrowsePhotographers from './pages/browse-photographers';
import Checkout from './pages/checkout/Checkout';
import DirectCheckout from './pages/checkout/DirectCheckout';
import BookingWaiting from './pages/booking-waiting/BookingWaiting';
import BookingPayment from './pages/booking-payment/BookingPayment';
import BookingPaymentCallback from './pages/booking-payment-callback/BookingPaymentCallback';
import BookingExpired from './pages/booking-expired/BookingExpired';
import PhotographerConfirmation from './pages/photographer-confirmation/PhotographerConfirmation';
import BookingAction from './pages/booking-action';
import PortfolioManagement from './pages/portfolio-management';
import PhotographerDashboard from './pages/photographer-dashboard';
import PhotographerResults from './pages/photographer-results';
import CustomerDashboard from './pages/customer-dashboard';
import DashboardLayout from './pages/customer-dashboard/layout/DashboardLayout';
import DashboardHome from './pages/customer-dashboard/index';
import CustomerBookings from './pages/customer-dashboard/Bookings';
import CustomerDeliveries from './pages/customer-dashboard/MyDeliveries';
import CustomerFavorites from './pages/customer-dashboard/Favorites';
import CustomerNotifications from './pages/customer-dashboard/Notifications';
import CustomerProfile from './pages/customer-dashboard/Profile';
import CustomerSettings from './pages/customer-dashboard/Settings';
import CustomerHelp from './pages/customer-dashboard/Help';
import AccountHub from './pages/customer-dashboard/AccountHub';
import DeleteAccount from './pages/customer-dashboard/DeleteAccount';
import AccountGuest from './pages/account/AccountGuest';
import GuestSettings from './pages/account/GuestSettings';
import LocationSettings from './pages/account/LocationSettings';
import NotificationsPage from './pages/notifications';
import FavoritesPage from './pages/favorites';
import AboutPage from './pages/about';
import TermsPage from './pages/terms';
import PrivacyPage from './pages/privacy';
import HeaderComponent from './pages/header-component';
import FooterComponent from './pages/footer-component';
import CustomerLogin from './pages/customer-login';
import CustomerRegistration from './pages/customer-registration';
import PhotographerLogin from './pages/photographer-login';
import PhotographerRegistration from './pages/photographer-registration';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Breadcrumb from './components/navigation/Breadcrumb';
import UnifiedMenu from './components/navigation/UnifiedMenu';
import MobileBottomNav from './components/navigation/MobileBottomNav';
import EmailTemplateManagement from './pages/email-template-management';
import EmailAutomationDashboard from './pages/email-automation-dashboard';
import EmailPreviewTesting from './pages/email-preview-testing';
import AdminAnalyticsDashboard from './pages/admin-analytics-dashboard';
import AdminRefunds from './pages/admin-refunds';
import AdminPhotographerVisibility from './pages/admin-photographer-visibility';
import ResetPassword from './pages/reset-password';
import AdminMasterDashboard from './pages/admin-master-dashboard';
import PhotographerCalendarManagement from './pages/photographer-calendar-management';
import AdminGuard from "./components/admin/AdminGuard";
import PhotographerJoin from './pages/photographer-join';
import PhotographerJoin2 from './pages/photographer-join2';
import PhotographerRegistrationWizard from './pages/photographer-registration-wizard';
import AdminHome from "./pages/admin";
import AdminLogin from "./pages/admin/AdminLogin";
import BookingsManagement from './pages/bookings-management';
import AdminBookingsManagement from './pages/admin-bookings-management';
import AdminPricing from './pages/admin-pricing';
import SupportPage from './pages/support';
import LocationPermission from './pages/onboarding/LocationPermission';
import NotificationPermission from './pages/onboarding/NotificationPermission';
import AlbumDownload from './pages/AlbumDownload';
import DeliveryPage from './pages/DeliveryPage';

// SAFE MODE: Auth System Fix - Do not render protected content until auth resolved
const AuthSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">جاري التحميل...</p>
    </div>
  </div>
);

// SAFE MODE: Auth System Fix - Home page with auth-aware loading state
const HomePage = () => {
  const { loading, authStatus, userProfile, AUTH_STATE } = useAuth();
  if (loading || authStatus === AUTH_STATE?.INITIALIZING) return <AuthSpinner />;
  return <Landing />;
};

// SAFE MODE: Auth System Fix - PhotographerRoute: unauthenticated → photographer-login; wrong role → home
const PhotographerRoute = ({ children }) => {
  const { user, userProfile, loading, authStatus, AUTH_STATE } = useAuth();
  if (loading || authStatus === AUTH_STATE?.INITIALIZING) return <AuthSpinner />;
  if (authStatus !== AUTH_STATE?.AUTHENTICATED || !user) {
    return <Navigate to="/photographer-login" replace />;
  }
  if (userProfile?.role !== 'photographer') {
    return <Navigate to="/" replace />;
  }
  return children;
};

// CustomerRoute: unauthenticated (شبكة ضعيفة / phantom logout / غير مسجّل) → الصفحة الرئيسية
const CustomerRoute = ({ children }) => {
  const { user, loading, authStatus, AUTH_STATE } = useAuth();
  if (loading || authStatus === AUTH_STATE?.INITIALIZING) return <AuthSpinner />;
  if (authStatus !== AUTH_STATE?.AUTHENTICATED || !user) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// مسار تسجيل الدخول حسب الدور (مشرف → admin مباشرة مؤقتاً، مصور → photographer-login، عميل → customer-login)
const getLoginPathForRoles = (allowedRoles) => {
  if (allowedRoles?.includes('admin')) return '/admin';
  if (allowedRoles?.includes('photographer')) return '/photographer-login';
  // العميل: توجيه للصفحة الرئيسية بدلاً من صفحة تسجيل جديد
  return '/';
};

// SAFE MODE: Auth System Fix - ProtectedRoute: only render when authStatus === authenticated and role allowed
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, userProfile, loading, authStatus, AUTH_STATE } = useAuth();

  if (loading || authStatus === AUTH_STATE?.INITIALIZING) {
    return <AuthSpinner />;
  }

  if (authStatus !== AUTH_STATE?.AUTHENTICATED || !user) {
    const loginPath = getLoginPathForRoles(allowedRoles);
    return <Navigate to={loginPath} replace />;
  }

  if (allowedRoles?.length && !allowedRoles?.includes(userProfile?.role)) {
    const loginPath = getLoginPathForRoles(allowedRoles);
    return <Navigate to={loginPath} replace />;
  }

  return children;
};

const ONBOARDING_COMPLETED_KEY = 'onboarding_completed';

/**
 * هل البيئة الحالية تستحق عرض الـ Onboarding؟
 * نعرضه فقط على: تطبيق Capacitor أصلي، أو PWA مثبّت، أو شاشة جوال (< 768px).
 * نتخطاه على متصفح ديسكتوب العادي لأن نوافذ الموقع والإشعارات تبدو غريبة هناك.
 */
function shouldShowOnboarding() {
  if (typeof window === 'undefined') return false;
  // تطبيق Capacitor أصلي
  if (window.Capacitor?.isNativePlatform?.() === true) return true;
  // PWA مثبّت (standalone / fullscreen)
  if (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches ||
    window.navigator?.standalone === true
  ) return true;
  // متصفح جوال (عرض أقل من 768px)
  if (window.innerWidth < 768) return true;
  return false;
}

// Onboarding guard: show onboarding only on first open; else render main app
const AppOrOnboarding = () => {
  const location = useLocation();
  const pathname = location.pathname || '';
  const isOnboardingPath = pathname.startsWith('/onboarding');

  // صفحة هبوط المصورين — مستقلة كلياً بدون AppLayout
  if (pathname === '/join' || pathname.startsWith('/join/')) {
    return <PhotographerJoin />;
  }
  if (pathname === '/join2' || pathname.startsWith('/join2/')) {
    return <PhotographerJoin2 />;
  }
  // معالج تسجيل المصورين الموحد (Wizard) — مستقل بدون AppLayout
  if (pathname === '/photographer-register' || pathname.startsWith('/photographer-register/')) {
    return <PhotographerRegistrationWizard />;
  }

  // SAFE MODE: delivery and album pages are opened in external browser where
  // onboarding_completed is never set — bypass onboarding for these public pages
  const isPublicDeepLink =
    pathname.startsWith('/delivery/') ||
    pathname.startsWith('/album-download/') ||
    pathname.startsWith('/booking-payment-callback') || // إعادة توجيه بعد 3DS — يجب ألا يُعاق بالـ onboarding
    pathname.startsWith('/booking-payment/') || // قد يُفتح داخل InAppBrowser بدون جلسة onboarding
    pathname.startsWith('/photographers/') || // ملف مصور مشترك — يُعرض مباشرة بدون onboarding
    pathname.startsWith('/photographer-register'); // معالج تسجيل المصورين الموحد
  const completed =
    typeof localStorage !== 'undefined' &&
    localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true';

  // ديسكتوب / متصفح عادي: تخطي الـ Onboarding كلياً وعلامته كمكتمل
  if (!shouldShowOnboarding() && !isOnboardingPath) {
    if (typeof localStorage !== 'undefined' && !completed) {
      localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
    }
    return <AppLayout />;
  }

  if (!completed && !isOnboardingPath && !isPublicDeepLink) {
    return <Navigate to="/onboarding/location" replace />;
  }
  if (completed && isOnboardingPath) {
    return <Navigate to="/" replace />;
  }
  if (isOnboardingPath) {
    return (
      <RouterRoutes>
        <Route path="/onboarding/location" element={<LocationPermission />} />
        <Route path="/onboarding/notifications" element={<NotificationPermission />} />
        <Route path="/onboarding" element={<Navigate to="/onboarding/location" replace />} />
        <Route path="*" element={<Navigate to="/onboarding/location" replace />} />
      </RouterRoutes>
    );
  }
  return <AppLayout />;
};

// App shell with global role-based redirect on initial load
const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, loading, authStatus, AUTH_STATE } = useAuth();

  // Global role-based redirect on app start (only when landing on "/")
  useEffect(() => {
    if (loading || authStatus === AUTH_STATE?.INITIALIZING) return;

    // Only handle initial landing on root path to avoid breaking deep links and protected routes
    if (location.pathname !== "/") return;

    // If not authenticated, root ("/") is already the correct landing page
    if (authStatus !== AUTH_STATE?.AUTHENTICATED || !user) return;
    // SAFE: no profile row (e.g. backfill not run) → send to complete profile
    if (!userProfile) {
      navigate('/customer-registration', { replace: true });
      return;
    }

    // Authenticated photographer → photographer dashboard
    if (userProfile.role === "photographer") {
      if (!location.pathname.startsWith("/photographer-dashboard")) {
        navigate("/photographer-dashboard", { replace: true });
      }
      return;
    }

    // Authenticated customer → stay on "/" (customer landing / browsing)
    if (userProfile.role === "customer") {
      return;
    }

    // Other roles (e.g. admin): do not auto-redirect here
  }, [loading, authStatus, AUTH_STATE, user, userProfile, location.pathname, navigate]);

  // وضع InAppBrowser: إخفاء كل عناصر التصفح عند فتح الصفحة داخل متصفح داخلي (inapp=1)
  const isInApp = new URLSearchParams(location.search).get('inapp') === '1';

  // إخفاء الهيدر العلوي (الشعار، لوحة التحكم، تسجيل الخروج) على الجوال والتابلت عند ظهور القائمة السفلية
  const BOTTOM_NAV_MAX_WIDTH = 1024;
  const [showTopHeader, setShowTopHeader] = useState(typeof window !== 'undefined' ? window.innerWidth > BOTTOM_NAV_MAX_WIDTH : true);
  useEffect(() => {
    const check = () => setShowTopHeader(window.innerWidth > BOTTOM_NAV_MAX_WIDTH);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // لوحة المصور تملك هيدرها الخاص — نخفي الهيدر العام لتجنب التكرار
  const isPhotographerDashboard = location.pathname.startsWith('/photographer-dashboard');
  // صفحات ثابتة عامة — تُخفى فيها القائمة الجانبية
  const isStaticPage =
    location.pathname === '/about' || location.pathname === '/terms' || location.pathname === '/privacy';
  const isLoginPage =
    location.pathname === '/photographer-login' || location.pathname === '/customer-login';

  return (
    <ErrorBoundary>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen">
        {!isInApp && <AppDownloadBanner />}
        {showTopHeader && !isPhotographerDashboard && !isInApp && (
          <>
            <ErrorBoundary>
              <Header />
            </ErrorBoundary>
            {/* Breadcrumb hidden globally */}
          </>
        )}
        {userProfile?.role === 'photographer' && showTopHeader && !isPhotographerDashboard && !isStaticPage && !isLoginPage && !isInApp && (
          <ErrorBoundary>
            <UnifiedMenu />
          </ErrorBoundary>
        )}
        <main className={[
          'flex-1',
          showTopHeader && !isPhotographerDashboard && !isInApp
            ? 'pt-[calc(64px+env(safe-area-inset-top,0px)+var(--app-download-banner-height,0px))]'
            : 'pt-[calc(env(safe-area-inset-top,0px)+var(--app-download-banner-height,0px))]',
          !location.pathname.startsWith('/admin') && !location.pathname.startsWith('/delivery/') && !isInApp
            ? 'pb-[calc(80px+env(safe-area-inset-bottom,0px))]'
            : '',
        ].filter(Boolean).join(' ')}>
          <ErrorBoundary>
            <RouterRoutes>
              {/* Public Routes - SAFE MODE: photographers redirect to dashboard */}
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/album-download/:token" element={<AlbumDownload />} />
              <Route path="/delivery/:token" element={<DeliveryPage />} />
              <Route path="/photographer-profile" element={<PhotographerProfile />} />
              <Route path="/photographers/:id" element={<PhotographerProfile />} />
              <Route path="/browse-photographers" element={<BrowsePhotographers />} />
              <Route path="/photographer-results" element={<PhotographerResults />} />
              <Route path="/header-component" element={<HeaderComponent />} />
              <Route path="/footer-component" element={<FooterComponent />} />
              
              {/* صفحة هبوط المصورين — مستقلة بدون nav/footer */}
              <Route path="/join" element={<PhotographerJoin />} />
              <Route path="/join2" element={<PhotographerJoin2 />} />

              {/* Auth Routes */}
              <Route path="/customer-login" element={<CustomerLogin />} />
              <Route path="/customer-registration" element={<CustomerRegistration />} />
              <Route path="/photographer-login" element={<PhotographerLogin />} />
              <Route path="/photographer-registration" element={<PhotographerRegistration />} />
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route path="/admin-forgot-password" element={<Navigate to="/admin-login" replace />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* SAFE MODE: Protected Customer Routes */}
              <Route 
                path="/customer-dashboard" 
                element={
                  <CustomerRoute>
                    <DashboardLayout />
                  </CustomerRoute>
                }
              >
                <Route index element={<DashboardHome />} />
                <Route path="bookings" element={<CustomerBookings />} />
                <Route path="my-photos" element={<CustomerDeliveries />} />
                <Route path="my-deliveries" element={<CustomerDeliveries />} />
                <Route path="favorites" element={<CustomerFavorites />} />
                <Route path="notifications" element={<CustomerNotifications />} />
                <Route path="profile" element={<CustomerProfile />} />
                <Route path="settings" element={<CustomerSettings />} />
                <Route path="account" element={<AccountHub />} />
                <Route path="delete-account" element={<DeleteAccount />} />
                <Route path="help" element={<CustomerHelp />} />
              </Route>
              <Route path="/account" element={<AccountGuest />} />
              <Route path="/settings" element={<GuestSettings />} />
              <Route path="/settings/location" element={<LocationSettings />} />
              {/* SAFE MODE: حجز عادي بدون إجبار تسجيل الدخول */}
              <Route 
                path="/booking-wizard" 
                element={<BookingWizard />} 
              />
              {/* SAFE MODE: حجز مباشر بدون إجبار تسجيل الدخول */}
              <Route 
                path="/new-booking" 
                element={<BookingWizard />} 
              />
              <Route 
                path="/checkout" 
                element={<Checkout />} 
              />
              <Route 
                path="/checkout/direct" 
                element={<DirectCheckout />} 
              />
              <Route 
                path="/booking-checkout" 
                element={<Checkout />} 
              />
              <Route 
                path="/booking-confirmation" 
                element={
                  <ProtectedRoute allowedRoles={['customer']}>
                    <BookingConfirmation />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/booking-waiting/:bookingId" 
                element={
                  <CustomerRoute>
                    <BookingWaiting />
                  </CustomerRoute>
                } 
              />
              <Route 
                path="/booking-payment/:bookingId" 
                element={<BookingPayment />}
              />
              <Route 
                path="/booking-payment-callback" 
                element={<BookingPaymentCallback />} 
              />
              <Route 
                path="/booking-expired/:bookingId" 
                element={
                  <ProtectedRoute allowedRoles={['customer']}>
                    <BookingExpired />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/photographer-confirmation/:bookingId" 
                element={
                  <PhotographerRoute>
                    <PhotographerConfirmation />
                  </PhotographerRoute>
                } 
              />
              {/* WhatsApp Magic Action Links - No authentication required */}
              <Route 
                path="/action/booking" 
                element={<BookingAction />} 
              />
              <Route 
                path="/bookings-management" 
                element={
                  <ProtectedRoute allowedRoles={['customer']}>
                    <BookingsManagement />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/notifications" 
                element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/support" element={<SupportPage />} />
              <Route
                path="/favorites" 
                element={
                  <ProtectedRoute allowedRoles={['customer']}>
                    <FavoritesPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* SAFE MODE: Protected Photographer Routes */}
              <Route
                path="/photographer-dashboard/*" 
                element={
                  <PhotographerRoute>
                    <PhotographerDashboard />
                  </PhotographerRoute>
                } 
              />
              <Route
                path="/portfolio-management" 
                element={<Navigate to="/photographer-dashboard/portfolio-management" replace />}
              />
              <Route
                path="/email-template-management" 
                element={
                  <PhotographerRoute>
                    <EmailTemplateManagement />
                  </PhotographerRoute>
                } 
              />
              <Route
                path="/email-automation-dashboard" 
                element={
                  <PhotographerRoute>
                    <EmailAutomationDashboard />
                  </PhotographerRoute>
                } 
              />
              <Route
                path="/email-preview-testing" 
                element={
                  <PhotographerRoute>
                    <EmailPreviewTesting />
                  </PhotographerRoute>
                } 
              />
              <Route
                path="/photographer-calendar-management" 
                element={
                  <PhotographerRoute>
                    <PhotographerCalendarManagement />
                  </PhotographerRoute>
                } 
              />
              
              {/* Admin Routes - دخول مباشر بدون تسجيل (مؤقت للاختبار) */}
              <Route path="/admin-analytics-dashboard" element={<AdminAnalyticsDashboard />} />
              <Route path="/admin-master-dashboard" element={<AdminMasterDashboard />} />
              
              {/* Admin Panel (NEW) */}
              <Route 
                path="/admin"
                element={
                  <AdminGuard>
                    <AdminHome />
                  </AdminGuard>
                }
              />
              <Route 
                path="/admin-bookings"
                element={
                  <AdminGuard>
                    <AdminBookingsManagement />
                  </AdminGuard>
                }
              />
              <Route 
                path="/admin-pricing"
                element={
                  <AdminGuard>
                    <AdminPricing />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin-refunds"
                element={
                  <AdminGuard>
                    <AdminRefunds />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin-photographer-visibility"
                element={
                  <AdminGuard>
                    <AdminPhotographerVisibility />
                  </AdminGuard>
                }
              />
              
              <Route path="*" element={<NotFound />} />
            </RouterRoutes>
          </ErrorBoundary>
        </main>
        {!location.pathname.startsWith('/admin') && !location.pathname.startsWith('/delivery/') && !isInApp && (
          <ErrorBoundary>
            <MobileBottomNav />
          </ErrorBoundary>
        )}
        {!isInApp && (
          <ErrorBoundary>
            <Footer />
          </ErrorBoundary>
        )}
      </div>
    </ErrorBoundary>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AppOrOnboarding />
    </BrowserRouter>
  );
};

export default Routes;